import { datadogRum } from '@datadog/browser-rum'
import { useEffect } from 'react'
import { Navigate } from 'react-router'

import { appMountPath } from './appMountPath'
import { resolveSessionLandingApp } from './landingApp'

const ENTITLEMENT_REDIRECT_ACTION = 'app entitlement redirect'

/**
 * Redirects to a resolved app root or to the no-access page when none is enabled.
 *
 * The single redirect every entry into the landing chain goes through: `Bootstrap`, `AppFallback`, and
 * the customer layout's unavailable-app branch (Atlas ADR-0015). Landing at the app's root rather than
 * the requested path is deliberate. The fallback crosses an app boundary, and a deeper path belongs
 * to the app that defined it.
 *
 * An entitlement-driven resolution also emits its own RUM action, distinct from ordinary defaulting,
 * so a rise in entitlement-mapping bugs is separable from users mistyping URLs (Atlas ADR-0005). The
 * effect depends on the redirect's own fields rather than on `resolution`, because resolution produces
 * a fresh object on each render. Depending on that object would report the same redirect again on any
 * unrelated re-render before the router commits the navigation, inflating the count this action exists
 * to keep clean.
 */
export const AppResolutionRedirect = ({
  customer,
  requestedAppId,
}: {
  customer: string
  requestedAppId?: string
}) => {
  const resolution = resolveSessionLandingApp(requestedAppId)

  const redirect = resolution.entitlementRedirect
  const reason = redirect?.reason
  const requestedApp = redirect?.requestedApp
  const resolvedApp = redirect?.resolvedApp

  useEffect(() => {
    if (reason === undefined) {
      return
    }

    datadogRum.addAction(ENTITLEMENT_REDIRECT_ACTION, {
      reason,
      ...(requestedApp === undefined ? {} : { requestedApp }),
      ...(resolvedApp === undefined ? {} : { resolvedApp }),
    })
  }, [reason, requestedApp, resolvedApp])

  return (
    <Navigate
      replace
      to={
        resolution.app === undefined
          ? '/unauthorized'
          : appMountPath(customer, resolution.app.id)
      }
    />
  )
}
